import { Invoice, InvoiceSortValue } from "@common/types/invoice";
import { InvoiceColumn, InvoiceView } from "@common/types/views/invoiceViews";
import { View } from "@common/types/views/views";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useAccrualEntityIdsWithLoading } from "@api/ledger.api";
import { useInvoiceAccrualMatchSummaries } from "@api/invoice.api";
import { useAccrualMatchSummaryMap } from "@components/accounting/transactionMatch/useAccrualMatchSummaryMap";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import {
    ViewTable,
    type ViewTableRowDragOut,
} from "@design-system/ViewTable/ViewTable";
import type { ViewTableLoadMore } from "@design-system/ViewTable/ViewTableLoadMore";
import { PeriodLockDatesProvider } from "@components/periodLock/PeriodLockDatesProvider";
import { useInvoiceRowHighlight } from "@components/tasks/useInvoiceRowHighlight";
import { useLoadedRowIdResolver } from "@components/tasks/useRefRowHighlight";
import styles from "../InvoicesView.module.scss";
import { createInvoicesViewConfig } from "./invoicesViewConfig";
import { InvoicesViewCallbacks } from "./types";

interface Props {
    view: InvoiceView;
    invoices: Invoice[] | null;
    selectedInvoiceId?: string;
    onInvoiceClick?: (invoice: Invoice) => void;
    sort?: InvoiceSortValue;
    onSortChange?: (sort: InvoiceSortValue) => void;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    isSchedulesEnabled?: boolean;
    prioritizedScheduleTemplateId?: string;
    onCreateSchedule?: InvoicesViewCallbacks["onCreateSchedule"];
    onScheduleApplied?: InvoicesViewCallbacks["onScheduleApplied"];
    onRequestScheduleConfirm?: InvoicesViewCallbacks["onRequestScheduleConfirm"];
    onRequestLifecycleAction?: InvoicesViewCallbacks["onRequestLifecycleAction"];
    onSelectInvoice?: InvoicesViewCallbacks["onSelectInvoice"];
    useFixedTopOffset?: boolean;
    onViewChange?: (view: View) => void;
    rowDragOut?: ViewTableRowDragOut<Invoice>;
    loadMore?: ViewTableLoadMore;
}

const getInvoiceSummaryId = (summary: { invoiceId: string }) =>
    summary.invoiceId;

export const InvoicesView: React.FC<Props> = ({
    view,
    invoices,
    selectedInvoiceId,
    onInvoiceClick,
    sort,
    onSortChange,
    emptyView = () => "No invoices found",
    footer,
    className,
    rowSelection,
    onRowSelectionChange,
    isSchedulesEnabled = false,
    prioritizedScheduleTemplateId,
    onCreateSchedule,
    onScheduleApplied,
    onRequestScheduleConfirm,
    onRequestLifecycleAction,
    onSelectInvoice,
    useFixedTopOffset = true,
    onViewChange,
    rowDragOut,
    loadMore,
}) => {
    const data = invoices ?? null;

    const handleRowClick = useCallback(
        (invoice?: Invoice) => {
            if (invoice) {
                onInvoiceClick?.(invoice);
            }
        },
        [onInvoiceClick],
    );

    const handleFocusedRowChange = useCallback(
        (
            _index: number | null,
            row: Invoice | null,
            source: "keyboard" | "click",
        ) => {
            if (
                source !== "keyboard" ||
                selectedInvoiceId === undefined ||
                !row ||
                row.id === selectedInvoiceId
            ) {
                return;
            }
            onInvoiceClick?.(row);
        },
        [selectedInvoiceId, onInvoiceClick],
    );

    const entityIds = useMemo(
        () => invoices?.map((invoice) => invoice.entityId) ?? [],
        [invoices],
    );
    const { accrualEntityIds, isLoading: isLoadingAccrualLedgers } =
        useAccrualEntityIdsWithLoading(entityIds);

    const scheduleTemplatesQuery = useScheduleTemplates({
        enabled: isSchedulesEnabled,
    });

    const invoiceIds = useMemo(
        () => invoices?.map((invoice) => invoice.id) ?? [],
        [invoices],
    );
    const isMatchColumnVisible = view.columns.some(
        (column) => column.id === InvoiceColumn.MATCH,
    );
    const accrualMatchSummariesQuery = useInvoiceAccrualMatchSummaries({
        invoiceIds,
        enabled: isMatchColumnVisible,
    });
    const accrualMatchSummaries = useAccrualMatchSummaryMap({
        summaries: accrualMatchSummariesQuery.data,
        getDocumentId: getInvoiceSummaryId,
    });

    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onInvoiceClick,
                onCreateSchedule,
                onScheduleApplied,
                onRequestScheduleConfirm,
                onRequestLifecycleAction,
                onSelectInvoice,
            },
            options: {
                scheduleTemplates: scheduleTemplatesQuery.data,
                prioritizedScheduleTemplateId,
                accrualMatchSummaries,
                accrualMatchSummariesStatus: accrualMatchSummariesQuery.status,
                readonly: view.options?.readonly,
            },
            accrualEntityIds,
            isLoadingAccrualLedgers,
        }),
        [
            onInvoiceClick,
            onCreateSchedule,
            onScheduleApplied,
            onRequestScheduleConfirm,
            onRequestLifecycleAction,
            onSelectInvoice,
            scheduleTemplatesQuery.data,
            prioritizedScheduleTemplateId,
            accrualMatchSummaries,
            accrualMatchSummariesQuery.status,
            view.options?.readonly,
            accrualEntityIds,
            isLoadingAccrualLedgers,
        ],
    );

    // Every invoice is selectable for the bulk-actions footer (Mark Void,
    // Mark Uncollectible, Change Counterparty, Download PDF). The importer-
    // only bulk "Edit" action is gated separately in InvoicesViewContainer,
    // which only offers it when every selected invoice is importer-sourced,
    // so it never receives non-importer invoices.
    const enableRowSelection = useCallback(() => true, []);
    const viewConfig = useMemo(
        () =>
            createInvoicesViewConfig({
                isSchedulesEnabled,
            }),
        [isSchedulesEnabled],
    );
    const isDetailsPanelOpen = selectedInvoiceId !== undefined;

    const resolveTableRowId = useLoadedRowIdResolver(data);
    const { viewTableRef, selectedRowId: focusedRefRowId } =
        useInvoiceRowHighlight({ resolveTableRowId });
    const visibleView = useMemo(
        () => ({
            ...view,
            columns: view.columns.filter((column) => {
                // `internal` only hides a column from the picker; a view saved
                // while the flag was on would still render it.
                if (
                    !isSchedulesEnabled &&
                    column.id === InvoiceColumn.SCHEDULE
                ) {
                    return false;
                }
                if (
                    isDetailsPanelOpen &&
                    (column.id === InvoiceColumn.EXTERNAL_LINK ||
                        column.id === InvoiceColumn.ENTITY)
                ) {
                    return false;
                }
                if (
                    column.id === InvoiceColumn.SELECT &&
                    onSelectInvoice == null
                ) {
                    return false;
                }
                return true;
            }),
        }),
        [isSchedulesEnabled, view, isDetailsPanelOpen, onSelectInvoice],
    );

    return (
        <PeriodLockDatesProvider entityIds={entityIds}>
            <ViewTable
                ref={viewTableRef}
                className={classNames(styles.invoicesView, className)}
                data={data}
                view={visibleView as View}
                config={viewConfig}
                selectedRowId={focusedRefRowId ?? selectedInvoiceId}
                onRowClick={handleRowClick}
                onFocusedRowChange={handleFocusedRowChange}
                sort={sort}
                onSortChange={onSortChange}
                emptyView={emptyView}
                fixedTopOffsetPx={useFixedTopOffset ? true : undefined}
                tableMeta={tableMeta}
                footer={footer}
                enableKeyboardShortcuts
                rowSelection={rowSelection}
                onRowSelectionChange={onRowSelectionChange}
                enableRowSelection={enableRowSelection}
                onViewChange={onViewChange}
                rowDragOut={rowDragOut}
                loadMore={loadMore}
            />
        </PeriodLockDatesProvider>
    );
};
